import React, { useCallback } from 'react';
import { Box, Stack } from '@mui/material';
import { colors, Typography } from '@sweep-io/sweep-design';
import { TruncatedTextTooltip } from '../TruncatedTextTooltip';
import { BuildDefinitionIllustration } from './BuildDefinitionIllustration';
import { useBuildDefinitions } from './useBuildDefinitions';
import { useSelector } from 'react-redux';
import {
  selectActiveChatId,
  selectBuildStreamingState,
} from '../../aiAgentCenter/agentsChatsReducer';
import { useAgentChatInstanceOptional } from '../../aiAgentCenter/AgentChatInstanceContext';
import { ChatInstance } from '../../aiAgentCenter/ChatInstance';
import { ShimmerOverlay } from '../../animations/ShimmerOverlay';

interface BuildDefinitionPreviewProps {
  buildDefinitionData: {
    buildDefinitionId: string;
  };
}

export const BuildDefinitionPreview: React.FC<BuildDefinitionPreviewProps> = ({
  buildDefinitionData,
}) => {
  const { openBuildDefinitionPanel } = useBuildDefinitions();

  const chatInstanceContext = useAgentChatInstanceOptional();
  const chatInstanceId = chatInstanceContext?.chatInstanceId ?? ChatInstance.agentsPage;
  const activeChatId = useSelector(selectActiveChatId(chatInstanceId));
  const buildStreamingState = useSelector(selectBuildStreamingState(activeChatId ?? ''));
  const isLoading =
    buildStreamingState?.buildDefinitionId === buildDefinitionData.buildDefinitionId;

  const handleClick = useCallback(() => {
    openBuildDefinitionPanel(buildDefinitionData.buildDefinitionId);
  }, [buildDefinitionData.buildDefinitionId, openBuildDefinitionPanel]);

  return (
    <Box
      sx={{
        containerType: 'inline-size',
        border: `1px solid ${colors.grey[300]}`,
        borderRadius: '12px',
        backgroundColor: 'white',
        width: '100%',
        height: '78px',
        transition: 'all 0.2s ease-in-out',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': {
          borderColor: colors.blue[500],
        },
      }}
      onClick={handleClick}
    >
      <ShimmerOverlay isAnimating={isLoading} duration={3} sx={{ padding: 2, height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
            paddingRight: '285px',
            '@container (max-width: 600px)': {
              paddingRight: 0,
            },
          }}
        >
          <TruncatedTextTooltip variant="body" color={colors.grey[700]}>
            Metadata Definition
          </TruncatedTextTooltip>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="caption">Detailed definition of your build request</Typography>
          </Stack>
        </Box>
        <Box
          sx={{
            position: 'absolute',
            right: '18px',
            bottom: '-7px',
            '@container (max-width: 600px)': {
              display: 'none',
            },
          }}
        >
          <BuildDefinitionIllustration />
        </Box>
      </ShimmerOverlay>
    </Box>
  );
};
